/**
 * Insider Integration — User Profile Management
 * Uses InsiderQueue pattern (type: 'user' + 'set_custom_identifier').
 */

import { insiderConfig } from './insider.config';
import { queuePush, queueCommit } from './insider';
import { normalizeCpfAndHash } from './insider.identity';
import { EInsiderQueueType } from './insider.types';
import type { InsiderCustomerProfile } from './insider.types';

/**
 * Set/update the Insider user profile with customer data.
 */
export async function setInsiderUserProfile(customer: InsiderCustomerProfile): Promise<void> {
  if (!insiderConfig.enabled) return;

  const cpfHash = await normalizeCpfAndHash(customer.cpf);

  // User attributes
  const userValue: Record<string, unknown> = {
    name: customer.nome || undefined,
    email: customer.email || undefined,
    phone_number: formatPhoneE164(customer.telefone),
    birthday: customer.dataNascimento || undefined,
    gender: mapGender(customer.genero),
    language: 'pt_BR',
    city: customer.cidade || undefined,
    email_optin: customer.email_optin,
    sms_optin: customer.sms_optin,
    whatsapp_optin: customer.whatsapp_optin,
    gdpr_optin: customer.gdpr_optin,
    custom: {
      cpf: cpfHash,
      estado: customer.estado || undefined,
    },
  };

  // Remove undefined values
  const cleaned = removeUndefined(userValue);

  queuePush({ type: EInsiderQueueType.user, value: cleaned });
  queuePush({ type: EInsiderQueueType.set_custom_identifier, value: { cpf: cpfHash } });
  queueCommit();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPhoneE164(phone: string): string | undefined {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 10) return `+55${digits}`;
  return undefined;
}

function mapGender(genero: string): string | undefined {
  if (!genero) return undefined;
  const lower = genero.toLowerCase();
  if (lower === 'masculino' || lower === 'm') return 'male';
  if (lower === 'feminino' || lower === 'f') return 'female';
  return 'other';
}

function removeUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'object' && !Array.isArray(value)) {
      result[key] = removeUndefined(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result;
}
